// /src/dataTypes/adminManagment/types_addressBook.ts

// Un contact porte deux canaux, tous deux facultatifs : l'email et l'identifiant
// WhatsApp (un numéro de téléphone). Un contact sans aucun des deux n'est
// joignable par personne — c'est ce que le formulaire d'ajout refuse.
export interface AddressBookContact {
    entityId    : number;
    contactType : string;  // "Person" | "LegalStructure"
    firstname   : string | null;
    lastname    : string | null;
    email       : string | null;
    whatsapp    : string | null;
}

/** Canal par lequel un contact est joint. */
export type ContactChannel = 'email' | 'whatsapp';

export const initializedAddressBookContact = (): AddressBookContact => ({
    entityId    : 0    ,
    contactType : ''   ,
    firstname   : null ,
    lastname    : null ,
    email       : null ,
    whatsapp    : null ,
});

/** La coordonnée d'un contact sur un canal donné, ou null s'il n'y est pas joignable. */
export const contactValueFor = (contact: AddressBookContact, channel: ContactChannel): string | null =>
    channel === 'whatsapp' ? contact.whatsapp : contact.email;

// ─────────────────────────────────────────────────────────────────────────────
// Destinataire d'un envoi
//
// Une adresse email et un numéro WhatsApp se ressemblent trop peu pour tenir
// dans une simple chaîne : il faut savoir PAR OÙ le message part. D'où ce couple
// canal + valeur, qui cohabite dans une seule zone de saisie — un envoi peut
// mélanger les deux.
//
// `label` porte le nom de la personne quand le destinataire vient du carnet : on
// affiche « Alice Martin » plutôt que son adresse. Saisi à la main, il n'y a pas
// de nom à afficher, et c'est la valeur brute qui apparaît.
// ─────────────────────────────────────────────────────────────────────────────

export interface Recipient {
    channel : ContactChannel;
    /** Ce qui part réellement : l'adresse, ou le numéro. */
    value   : string;
    /** Nom du contact, uniquement s'il vient du carnet d'adresses. */
    label?  : string;
}

/**
 * Identité d'un destinataire, pour dédoublonner.
 *
 * Le canal en fait partie : la même personne peut être destinataire deux fois,
 * une par canal, et ce sont bien deux envois distincts.
 */
export const recipientKey = (recipient: Recipient): string =>
    `${recipient.channel}:${recipient.value.trim().toLowerCase()}`;

/** Ce que l'opérateur lit sur la puce : le nom s'il vient du carnet, la coordonnée sinon. */
export const recipientDisplay = (recipient: Recipient): string =>
    recipient.label !== undefined && recipient.label.trim() !== ''
        ? recipient.label.trim()
        : recipient.value;

/** Le destinataire correspondant à une coordonnée du carnet, nom compris. */
export const recipientFromContact = (contact: AddressBookContact, channel: ContactChannel): Recipient | null => {

    const value = contactValueFor(contact, channel);

    if (value === null || value.trim() === '') { return null; }

    const nom = [contact.firstname, contact.lastname].filter(p => p !== null && p !== '').join(' ').trim();

    return { channel, value, label: nom !== '' ? nom : undefined };
};